import { useMemo } from 'react';
import SearchIcon from '../../assets/icons/search.svg?react';
import {
  useGetCustomersQuery,
  useGetMachinesQuery,
  useGetPlantsQuery,
} from '../../gql/generated';
import {
  EDGE_DEVICE_CONNECTION_STATUS_OPTIONS,
  EDGE_DEVICE_STATUS_OPTIONS,
  useMachineFilters,
} from '../../hooks/useMachineFilters';
import { Input } from '../Input';
import { MobileSearchableSelect } from '../MobileSearchableSelect';
import { STATUS_OPTIONS } from './StatusFilter';

/**
 * Mobile filters for machines page.
 * Dynamically generates filters from machine data - automatically includes new EDMS properties.
 */
export function MobileFilters() {
  const {
    filters,
    setFilter,
    setDisplayName,
    setSerial,
    setPartNumber,
    setCustomers,
    setPlants,
    setFamilies,
    setStatuses,
    setCountryCodes,
    setCountries,
    setCities,
    setStates,
  } = useMachineFilters();

  // Fetch data for filters
  const { data: customersData, loading: customersLoading } =
    useGetCustomersQuery({
      fetchPolicy: 'cache-and-network',
    });

  const { data: plantsData, loading: plantsLoading } = useGetPlantsQuery({
    fetchPolicy: 'cache-and-network',
  });

  const { data: machinesData, loading: machinesLoading } = useGetMachinesQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Transform options - dynamically generated from the data
  const customerOptions = useMemo(
    () =>
      (customersData?.customers || []).map((c) => ({
        value: c.id,
        label: c.displayName || c.id,
      })),
    [customersData]
  );

  const plantOptions = useMemo(
    () =>
      (plantsData?.plants || []).map((p) => ({
        value: p.id,
        label: p.displayName || p.id,
      })),
    [plantsData]
  );

  // Dynamic options derived from machine data
  const familyOptions = useMemo(
    () =>
      Array.from(
        new Set(
          machinesData?.machines?.rows
            .map((m) => m.family)
            .filter((v): v is string => !!v) || []
        )
      )
        .sort()
        .map((v) => ({ value: v, label: v })),
    [machinesData]
  );

  const countryCodeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          machinesData?.machines?.rows
            .map((m) => m.address?.countryCode)
            .filter((v): v is string => !!v) || []
        )
      )
        .sort()
        .map((v) => ({ value: v, label: v })),
    [machinesData]
  );

  const countryOptions = useMemo(
    () =>
      Array.from(
        new Set(
          machinesData?.machines?.rows
            .map((m) => m.address?.country)
            .filter((v): v is string => !!v) || []
        )
      )
        .sort()
        .map((v) => ({ value: v, label: v })),
    [machinesData]
  );

  const cityOptions = useMemo(
    () =>
      Array.from(
        new Set(
          machinesData?.machines?.rows
            .map((m) => m.address?.city)
            .filter((v): v is string => !!v) || []
        )
      )
        .sort()
        .map((v) => ({ value: v, label: v })),
    [machinesData]
  );

  const stateOptions = useMemo(
    () =>
      Array.from(
        new Set(
          machinesData?.machines?.rows
            .map((m) => m.address?.state)
            .filter((v): v is string => !!v) || []
        )
      )
        .sort()
        .map((v) => ({ value: v, label: v })),
    [machinesData]
  );

  return (
    <>
      {/* Search filters */}
      <MobileSearchFilter
        label='Machine Name'
        value={filters.displayName}
        onChange={setDisplayName}
        placeholder='Search machines...'
      />

      <MobileSearchFilter
        label='Serial'
        value={filters.serial}
        onChange={setSerial}
        placeholder='Search serial...'
      />

      <MobileSearchFilter
        label='Part Number'
        value={filters.partNumber}
        onChange={setPartNumber}
        placeholder='Search part number...'
      />

      {/* Multi-select filters */}
      <MobileSelectFilter
        label='Customer'
        options={customerOptions}
        selectedValues={filters.customers}
        onChange={setCustomers}
        placeholder={customersLoading ? 'Loading...' : 'Select customers'}
      />

      <MobileSelectFilter
        label='Plant'
        options={plantOptions}
        selectedValues={filters.plants}
        onChange={setPlants}
        placeholder={plantsLoading ? 'Loading...' : 'Select plants'}
      />

      <MobileSelectFilter
        label='Family'
        options={familyOptions}
        selectedValues={filters.families}
        onChange={setFamilies}
        placeholder={machinesLoading ? 'Loading...' : 'Select families'}
      />

      <MobileSelectFilter
        label='Machine state'
        options={STATUS_OPTIONS}
        selectedValues={filters.statuses}
        onChange={setStatuses}
        placeholder='Select statuses'
      />

      <MobileSelectFilter
        label='Country Code'
        options={countryCodeOptions}
        selectedValues={filters.countryCodes}
        onChange={setCountryCodes}
        placeholder={machinesLoading ? 'Loading...' : 'Select country codes'}
      />

      <MobileSelectFilter
        label='Country'
        options={countryOptions}
        selectedValues={filters.countries}
        onChange={setCountries}
        placeholder={machinesLoading ? 'Loading...' : 'Select countries'}
      />

      <MobileSelectFilter
        label='City'
        options={cityOptions}
        selectedValues={filters.cities}
        onChange={setCities}
        placeholder={machinesLoading ? 'Loading...' : 'Select cities'}
      />

      <MobileSelectFilter
        label='State/Province'
        options={stateOptions}
        selectedValues={filters.states}
        onChange={setStates}
        placeholder={machinesLoading ? 'Loading...' : 'Select states'}
      />

      <MobileSelectFilter
        label='Edge Device Provisioning'
        options={EDGE_DEVICE_STATUS_OPTIONS}
        selectedValues={filters.edgeDeviceStatuses}
        onChange={(values) => setFilter('edgeDeviceStatuses', values)}
        placeholder='Select provisioning state'
      />

      <MobileSelectFilter
        label='Edge Device Connection'
        options={EDGE_DEVICE_CONNECTION_STATUS_OPTIONS}
        selectedValues={filters.edgeDeviceConnectionStatuses}
        onChange={(values) => setFilter('edgeDeviceConnectionStatuses', values)}
        placeholder='Select connection state'
      />

      <MobileSearchFilter
        label='Edge Device ID (IoT)'
        value={filters.edgeDeviceGuid}
        onChange={(value) => setFilter('edgeDeviceGuid', value)}
        placeholder='Search edge device ID...'
      />
    </>
  );
}

// Generic mobile search filter component
interface MobileSearchFilterProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

function MobileSearchFilter({
  label,
  value,
  onChange,
  placeholder,
}: MobileSearchFilterProps) {
  const hasValue = value.length > 0;

  return (
    <div className='flex flex-col gap-2'>
      <label className='size-p2 text-secondary font-medium'>{label}</label>
      <div className='relative'>
        <span
          className='text-disabled pointer-events-none absolute top-1/2 left-2
            flex h-4 w-4 -translate-y-1/2 items-center justify-center
            [&_path]:fill-current [&_svg]:fill-current'
        >
          <SearchIcon />
        </span>
        <Input
          type='text'
          placeholder={placeholder || 'Search...'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          variant={hasValue ? 'default' : 'inactive'}
          className='w-full pl-8'
        />
      </div>
    </div>
  );
}

// Generic mobile select filter component
interface MobileSelectFilterProps {
  label: string;
  options: { value: string; label: string }[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

function MobileSelectFilter({
  label,
  options,
  selectedValues,
  onChange,
  placeholder,
}: MobileSelectFilterProps) {
  return (
    <div className='flex flex-col gap-2'>
      <label className='size-p2 text-secondary font-medium'>{label}</label>
      <div className='w-full min-w-0'>
        <MobileSearchableSelect
          options={options}
          selectedValues={selectedValues}
          onSelectionChange={onChange}
          placeholder={placeholder || `Select ${label.toLowerCase()}`}
          label={label}
        />
      </div>
    </div>
  );
}
